import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { useAuth } from '../auth/AuthContext';
import { authFetch } from '../auth/apiFetch';
import logo from '../jkabrasillogo-horizontal.png';

const fieldStyle = { marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '6px' };
const fullWidth = { width: '100%' };

export default function LoginPage() {
    const { login } = useAuth();
    const [step, setStep] = useState('login'); // 'login' | 'new-password' | 'forgot' | 'confirm-forgot'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [code, setCode] = useState('');
    const [session, setSession] = useState('');
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [loading, setLoading] = useState(false);

    function goTo(s) {
        setError('');
        setInfo('');
        setStep(s);
    }

    async function handleLogin(e) {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await authFetch('/auth/login', {
                method: 'POST',
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.message || 'Falha ao autenticar. Verifique suas credenciais.');
                return;
            }
            if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
                setSession(data.session);
                setNewPassword('');
                setConfirmNewPassword('');
                goTo('new-password');
                return;
            }
            // Backend setou o cookie httpOnly; body retorna { email, groups }
            login({ email: data.email, groups: data.groups });
        } catch {
            setError('Erro de conexão. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }

    async function handleChangePassword(e) {
        e.preventDefault();
        if (newPassword !== confirmNewPassword) {
            setError('As senhas não coincidem.');
            return;
        }
        setError('');
        setLoading(true);
        try {
            const res = await authFetch('/auth/respond-challenge', {
                method: 'POST',
                body: JSON.stringify({ email, session, newPassword }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.message || 'Falha ao alterar a senha.');
                return;
            }
            login({ email: data.email, groups: data.groups });
        } catch {
            setError('Erro de conexão. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }

    async function handleForgotPassword(e) {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await authFetch('/auth/forgot-password', {
                method: 'POST',
                body: JSON.stringify({ email }),
            });
            if (!res.ok) {
                const data = await res.json();
                setError(data.message || 'Falha ao solicitar redefinição de senha.');
                return;
            }
            setInfo('Código enviado para seu e-mail. Verifique sua caixa de entrada.');
            setCode('');
            setNewPassword('');
            setConfirmNewPassword('');
            setStep('confirm-forgot');
        } catch {
            setError('Erro de conexão. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }

    async function handleConfirmForgotPassword(e) {
        e.preventDefault();
        if (newPassword !== confirmNewPassword) {
            setError('As senhas não coincidem.');
            return;
        }
        setError('');
        setLoading(true);
        try {
            const res = await authFetch('/auth/confirm-forgot-password', {
                method: 'POST',
                body: JSON.stringify({ email, confirmationCode: code, newPassword }),
            });
            if (!res.ok) {
                const data = await res.json();
                setError(data.message || 'Falha ao redefinir a senha.');
                return;
            }
            setPassword('');
            setInfo('Senha redefinida com sucesso. Faça login com a nova senha.');
            setStep('login');
        } catch {
            setError('Erro de conexão. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '80px', minHeight: '100vh', background: '#f5f5f5' }}>
            <img src={logo} alt="JKA Brasil" width="260" style={{ marginBottom: '32px' }} />

            {step === 'login' && (
                <Card title="Entrar" style={{ width: '380px' }}>
                    <form onSubmit={handleLogin}>
                        <div style={fieldStyle}>
                            <label htmlFor="login-email">E-mail</label>
                            <InputText id="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required style={fullWidth} />
                        </div>
                        <div style={fieldStyle}>
                            <label htmlFor="login-password">Senha</label>
                            <Password id="login-password" value={password} onChange={e => setPassword(e.target.value)} required feedback={false} toggleMask style={fullWidth} inputStyle={fullWidth} />
                        </div>
                        {error && <Message severity="error" text={error} style={{ marginBottom: '12px', width: '100%' }} />}
                        {info && <Message severity="success" text={info} style={{ marginBottom: '12px', width: '100%' }} />}
                        <Button type="submit" label="Entrar" loading={loading} style={fullWidth} />
                        <Button type="button" label="Esqueci minha senha" className="p-button-text" onClick={() => goTo('forgot')} style={{ ...fullWidth, marginTop: '8px' }} />
                    </form>
                </Card>
            )}

            {step === 'new-password' && (
                <Card title="Defina sua nova senha" style={{ width: '380px' }}>
                    <p style={{ marginBottom: '16px' }}>É necessário definir uma nova senha no primeiro acesso.</p>
                    <form onSubmit={handleChangePassword}>
                        <div style={fieldStyle}>
                            <label>Nova senha</label>
                            <Password value={newPassword} onChange={e => setNewPassword(e.target.value)} required style={fullWidth} inputStyle={fullWidth} />
                        </div>
                        <div style={fieldStyle}>
                            <label>Confirmar nova senha</label>
                            <Password value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} required feedback={false} toggleMask style={fullWidth} inputStyle={fullWidth} />
                        </div>
                        {error && <Message severity="error" text={error} style={{ marginBottom: '12px', width: '100%' }} />}
                        <Button type="submit" label="Salvar nova senha" loading={loading} style={fullWidth} />
                    </form>
                </Card>
            )}

            {step === 'forgot' && (
                <Card title="Redefinir senha" style={{ width: '380px' }}>
                    <p style={{ marginBottom: '16px' }}>Informe seu e-mail para receber o código de redefinição.</p>
                    <form onSubmit={handleForgotPassword}>
                        <div style={fieldStyle}>
                            <label>E-mail</label>
                            <InputText type="email" value={email} onChange={e => setEmail(e.target.value)} required style={fullWidth} />
                        </div>
                        {error && <Message severity="error" text={error} style={{ marginBottom: '12px', width: '100%' }} />}
                        <Button type="submit" label="Enviar código" loading={loading} style={fullWidth} />
                        <Button type="button" label="Voltar ao login" className="p-button-text" onClick={() => goTo('login')} style={{ ...fullWidth, marginTop: '8px' }} />
                    </form>
                </Card>
            )}

            {step === 'confirm-forgot' && (
                <Card title="Confirmar nova senha" style={{ width: '380px' }}>
                    <form onSubmit={handleConfirmForgotPassword}>
                        <div style={fieldStyle}>
                            <label>Código recebido por e-mail</label>
                            <InputText value={code} onChange={e => setCode(e.target.value)} required style={fullWidth} />
                        </div>
                        <div style={fieldStyle}>
                            <label>Nova senha</label>
                            <Password value={newPassword} onChange={e => setNewPassword(e.target.value)} required style={fullWidth} inputStyle={fullWidth} />
                        </div>
                        <div style={fieldStyle}>
                            <label>Confirmar nova senha</label>
                            <Password value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} required feedback={false} toggleMask style={fullWidth} inputStyle={fullWidth} />
                        </div>
                        {error && <Message severity="error" text={error} style={{ marginBottom: '12px', width: '100%' }} />}
                        {info && <Message severity="info" text={info} style={{ marginBottom: '12px', width: '100%' }} />}
                        <Button type="submit" label="Redefinir senha" loading={loading} style={fullWidth} />
                        <Button type="button" label="Voltar ao login" className="p-button-text" onClick={() => goTo('login')} style={{ ...fullWidth, marginTop: '8px' }} />
                    </form>
                </Card>
            )}
        </div>
    );
}
